import type { Scene } from "@babylonjs/core/scene";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { getAssetManager, type InstantiatedModel } from "../../systems/AssetManager";
import { freezeStaticMeshes } from "../staticOptimization";
import { tagAs } from "../PlaceholderTag";
import type { ConservatorySurfaces } from "./ConservatorySurfaces";

export async function loadConservatoryScenery(
  scene: Scene, shadows: ShadowGenerator, surfaces: ConservatorySurfaces,
): Promise<InstantiatedModel | null> {
  const fallbacks = [surfaces.ground, ...surfaces.platforms];
  let instance: InstantiatedModel | null = null;
  try {
    instance = await getAssetManager().instantiateSharedModel(
      scene, "/assets/models/environment/conservatory_glasshouse.glb",
    );
    if (scene.isDisposed) { instance.dispose(); return null; }
    const names = ["Conservatory_ground_top", ...surfaces.platforms.map(p => "DeckTop_"+p.name)];
    const tops = names.map(name => instance!.meshes.find(m =>
      m.name.replace(/^(Clone of )+/, "") === name && m.getTotalVertices() > 0,
    ));
    if (tops.some(m => !m)) throw new Error("Incomplete Conservatory surface artwork");
    for (const mesh of instance.meshes) {
      mesh.isPickable = false;
      mesh.receiveShadows = true;
      tagAs(mesh, "final");
    }
    // Rear architecture cannot inflate the playable shadow volume. Only
    // the seven exact decks cast; NPC/actor owners register their own skins.
    const decks = tops.slice(1).filter(m => m !== undefined);
    for (const mesh of decks) shadows.addShadowCaster(mesh);
    // Later puzzle/NPC lights still need to compile their material defines.
    freezeStaticMeshes(instance.meshes, { freezeMaterials: false });
    for (const surface of fallbacks) surface.isVisible = false;
    const owned = instance;
    let disposed = false;
    return {
      ...owned,
      dispose() {
        if (disposed) return;
        disposed = true;
        for (const mesh of decks) shadows.removeShadowCaster(mesh);
        owned.dispose();
        for (const surface of fallbacks) if (!surface.isDisposed()) surface.isVisible = true;
      },
    };
  } catch (error) {
    instance?.dispose();
    console.warn("[Conservatory] Scenery unavailable; keeping opaque physical surfaces:", error);
    return null;
  }
}
